import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MeResponse, ServerMessage } from '@pk/shared';
import { useTranslation } from 'react-i18next';
import { API_URL, MOCK, api } from './client';
import { getToken } from './token';
import { invalidateGame, qk } from './hooks';
import { useUi } from '../store';

/** Reconnecting game socket. Mounted once inside the authenticated shell. */
export function useGameSocket() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    if (MOCK) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ui = useUi.getState();

    // Coalesce bursts of server messages into a single round of refetches (a burst of hundreds of
    // parallel requests exhausts the browser's connection pool).
    const wanted = new Set<'day' | 'game' | 'chronicle' | 'chat'>();
    // Channels touched by 'chat' messages since the last flush: refetched once each, deduplicated.
    const dirtyChatChannels = new Set<string>();
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = (what: 'day' | 'game' | 'chronicle' | 'chat') => {
      wanted.add(what);
      if (flushTimer) return;
      flushTimer = setTimeout(async () => {
        flushTimer = undefined;
        const w = new Set(wanted);
        wanted.clear();
        if (w.has('day')) {
          // The house may have ended (extinct / inactivity): learn that first so we do not fetch a house that is gone.
          await qc.invalidateQueries({ queryKey: qk.me });
          if (!qc.getQueryData<MeResponse>(qk.me)?.houseId) return;
        }
        if (w.has('game')) invalidateGame(qc);
        if (w.has('day')) {
          void qc.invalidateQueries({ queryKey: qk.world });
          void qc.invalidateQueries({ queryKey: qk.house });
          void qc.invalidateQueries({ queryKey: ['character'] });
          void qc.invalidateQueries({ queryKey: ['title'] });
        }
        if (w.has('chronicle')) {
          void qc.invalidateQueries({ queryKey: qk.house });
          void qc.invalidateQueries({ queryKey: ['chronicle'] });
        }
        if (w.has('chat')) {
          // One refetch of the channel list (unread badges + last message) per burst...
          void qc.invalidateQueries({ queryKey: qk.chat });
          // ...and one refetch per channel that actually saw a message, deduplicated.
          for (const channelId of dirtyChatChannels) void qc.invalidateQueries({ queryKey: qk.chatMessages(channelId) });
          dirtyChatChannels.clear();
        }
      }, 400);
    };

    const connect = () => {
      const token = getToken();
      if (!token || closed) return;
      // API_URL may be empty (same-origin via the dev proxy / reverse proxy): derive the socket URL from the page then.
      const base = API_URL || location.origin;
      const url = `${base.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`;
      ws = new WebSocket(url);
      ws.onopen = () => { attempt = 0; ui.setWsConnected(true); };
      ws.onmessage = (ev) => {
        let msg: ServerMessage;
        try { msg = JSON.parse(String(ev.data)) as ServerMessage; } catch { return; }
        switch (msg.type) {
          case 'day':
            // After downtime the server announces every caught-up day in a burst; refetch once per burst.
            scheduleRefresh('day');
            break;
          case 'command_result': {
            const s = useUi.getState();
            if (!s.toastedCommands.has(msg.commandId)) {
              s.toastedCommands.add(msg.commandId);
              s.pushToast(msg.result.ok ? 'info' : 'error', msg.result.ok ? t('command.done') : msg.result.error ?? t('command.rejected'));
            }
            scheduleRefresh('game');
            break;
          }
          case 'chronicle':
            msg.entries.filter((e) => e.important).forEach((e) => useUi.getState().pushToast('chronicle', e.text));
            scheduleRefresh('chronicle');
            break;
          case 'hello':
            break;
          case 'chat':
            // No toasts: the chat screen reacts live. A busy channel bursts one message per send,
            // so park the channelId and let the 400 ms flush coalesce the refetches.
            dirtyChatChannels.add(msg.channelId);
            scheduleRefresh('chat');
            break;
        }
      };
      ws.onclose = () => {
        if (closed) return; // a superseded socket must not flip the flag of its successor
        ui.setWsConnected(false);
        const wait = Math.min(30_000, 1000 * 2 ** attempt) + Math.random() * 500;
        attempt++;
        timer = setTimeout(connect, wait);
      };
      ws.onerror = () => ws?.close();
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      clearTimeout(flushTimer);
      // Closing a socket that is still connecting logs a browser warning (React StrictMode mounts twice in dev).
      if (ws && ws.readyState === WebSocket.CONNECTING) { const s = ws; s.onopen = () => s.close(); s.onmessage = null; } else ws?.close();
      ui.setWsConnected(false);
    };
  }, [qc, t]);
}

/** POST /api/heartbeat on open/foreground and every 5 minutes while visible. */
export function useHeartbeat(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let interval: ReturnType<typeof setInterval> | undefined;
    const beat = () => { api.heartbeat().then(() => qc.invalidateQueries({ queryKey: qk.house })).catch(() => undefined); };
    const start = () => { beat(); clearInterval(interval); interval = setInterval(beat, 5 * 60_000); };
    const stop = () => { clearInterval(interval); interval = undefined; };
    const onVis = () => (document.visibilityState === 'visible' ? start() : stop());
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); stop(); };
  }, [enabled, qc]);
}
